import { MaterialCommunityIcons } from "@expo/vector-icons";
import { useState } from "react";
import { Pressable, StyleSheet, View } from "react-native";
import { Text } from "react-native-paper";

import { useContextVisibility } from "@/components/visit/ContextVisibility";
import type { ContextHint } from "@/lib/api";
import { formatShortDate } from "@/lib/context-checks";
import { groupContextHints, type HintEvidence } from "@/lib/context-hint-groups";
import { colors, fonts, iconTints, radius, spacing } from "@/lib/theme";

interface HintsProps {
    hints: ContextHint[];
    answer?: string;
    options?: ReadonlyArray<{ value: string; text: string }> | null;
    onOpen: (hint: ContextHint) => void;
}

/** Additional context stays inline; answer suggestions belong to the completion review. */
export function ContextHintBlock({ hints, answer, options, onOpen }: HintsProps) {
    const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
    const visibility = useContextVisibility();
    const visibleHints = hints.filter((hint) => visibility.visible(String(hint.questionId)));
    const groups = groupContextHints(visibleHints, answer, options ?? []).filter(
        (group) => group.additional.length > 0
    );
    const evidence = (entries: HintEvidence[]) =>
        entries.map(({ hint, clause, reasoning }, index) => {
            const date = formatShortDate(hint.observedAt);
            const sourceName =
                hint.sourceType === "questionnaire" ? "Patient questionnaire" : hint.sourceLabel;
            const reported =
                hint.sourceType === "questionnaire"
                    ? (hint.options?.find((option) => option.clause === clause)?.text ?? clause)
                    : clause;
            const source = `${sourceName}${date && !hint.sourceLabel.endsWith(date) ? ` · ${date}` : ""}`;
            return (
                <Pressable
                    key={`${hint.itemIds.join("-")}-${index}`}
                    onPress={hint.sourceViewEnabled === false ? undefined : () => onOpen(hint)}
                    disabled={hint.sourceViewEnabled === false}
                    accessibilityRole={hint.sourceViewEnabled === false ? undefined : "button"}
                    accessibilityLabel={
                        hint.sourceViewEnabled === false
                            ? `${reported}. ${source}`
                            : `${reported}. Open source: ${source}`
                    }
                    style={styles.evidence}
                >
                    <Text style={styles.bullet}>•</Text>
                    <View style={styles.evidenceBody}>
                        <Text style={styles.evidenceText}>
                            {reported}
                            {" — "}
                            <Text
                                style={[
                                    styles.source,
                                    hint.sourceViewEnabled === false && {
                                        color: colors.textSecondary
                                    }
                                ]}
                            >
                                {source}
                            </Text>
                        </Text>
                        {hint.sourceType !== "previous_visit" && reasoning?.trim() ? (
                            <Text style={styles.reasoning}>Why: {reasoning}</Text>
                        ) : null}
                    </View>
                </Pressable>
            );
        });
    if (!groups.length) {
        return null;
    }
    return (
        <View style={styles.block} testID="context-answer-hints">
            {groups.map((group) => {
                const expanded = !collapsed[group.key];
                const notice = [
                    group.differs ? "Differs from current answer" : "",
                    group.conflict ? "Sources contradict each other" : ""
                ]
                    .filter(Boolean)
                    .join(". ");
                return (
                    <View
                        key={group.key}
                        style={styles.card}
                        testID={`context-answer-${group.key}`}
                    >
                        <Pressable
                            style={styles.heading}
                            accessibilityRole="button"
                            accessibilityState={{ expanded }}
                            accessibilityLabel={`${group.label}${notice ? `. ${notice}` : ""}`}
                            accessibilityHint={
                                expanded ? "Collapse hint details" : "Expand hint details"
                            }
                            onPress={() =>
                                setCollapsed((current) => ({
                                    ...current,
                                    [group.key]: !current[group.key]
                                }))
                            }
                        >
                            {group.positive.length > 0 ? (
                                <MaterialCommunityIcons
                                    name="check"
                                    size={18}
                                    color={colors.success600}
                                    accessibilityLabel="Selected in sources"
                                />
                            ) : null}
                            {group.negative.length > 0 ? (
                                <MaterialCommunityIcons
                                    name="close"
                                    size={18}
                                    color={colors.error600}
                                    accessibilityLabel="Not selected in sources"
                                />
                            ) : null}
                            <Text style={styles.label}>{group.label}</Text>
                            {notice ? (
                                <MaterialCommunityIcons
                                    name="alert-outline"
                                    size={18}
                                    color={colors.warning600}
                                    accessibilityLabel={notice}
                                />
                            ) : null}
                            <MaterialCommunityIcons
                                name={expanded ? "chevron-up" : "chevron-down"}
                                size={20}
                                color={iconTints.sky.fg}
                            />
                        </Pressable>
                        {expanded ? (
                            <View style={styles.details}>
                                {group.conflict ? (
                                    <Text style={styles.stateLabel}>Selected in sources</Text>
                                ) : null}
                                {evidence(group.positive)}
                                {group.conflict ? (
                                    <Text style={styles.stateLabel}>Not selected in sources</Text>
                                ) : null}
                                {evidence(group.negative)}
                                {evidence(group.additional)}
                                {group.differs ? (
                                    <View style={styles.noticeFooter}>
                                        <MaterialCommunityIcons
                                            name="alert-circle-outline"
                                            size={14}
                                            color="#806B46"
                                        />
                                        <Text style={styles.noticeText}>
                                            Differs from current answer
                                        </Text>
                                    </View>
                                ) : null}
                                {group.conflict ? (
                                    <View style={styles.noticeFooter}>
                                        <MaterialCommunityIcons
                                            name="alert-circle-outline"
                                            size={14}
                                            color="#806B46"
                                        />
                                        <Text style={styles.noticeText}>
                                            Sources contradict each other
                                        </Text>
                                    </View>
                                ) : null}
                            </View>
                        ) : null}
                    </View>
                );
            })}
        </View>
    );
}

const styles = StyleSheet.create({
    block: { marginTop: spacing.sm, gap: spacing.sm },
    card: {
        paddingVertical: spacing.sm,
        paddingHorizontal: spacing.md,
        borderRadius: radius.sm,
        backgroundColor: iconTints.sky.bg,
        gap: spacing.sm
    },
    details: { gap: 6 },
    heading: { flexDirection: "row", alignItems: "center", gap: 6, minHeight: 32 },
    stateLabel: {
        marginLeft: 24,
        fontFamily: fonts.medium,
        fontSize: 11,
        color: colors.textSecondary
    },
    label: {
        flex: 1,
        fontFamily: fonts.semiBold,
        fontSize: 13,
        lineHeight: 19,
        color: colors.textPrimary
    },
    evidence: {
        marginLeft: 24,
        flexDirection: "row",
        alignItems: "flex-start",
        gap: 6,
        paddingVertical: 3
    },
    bullet: { fontSize: 12, lineHeight: 18, color: iconTints.sky.fg },
    evidenceBody: { flex: 1, gap: 3 },
    reasoning: {
        fontFamily: fonts.regular,
        fontSize: 11,
        lineHeight: 16,
        color: colors.textSecondary
    },
    evidenceText: {
        flex: 1,
        fontFamily: fonts.regular,
        fontSize: 12,
        lineHeight: 18,
        color: colors.textSecondary
    },
    source: { fontFamily: fonts.semiBold, color: iconTints.sky.fg },
    noticeFooter: {
        backgroundColor: "#FFF5E2",
        borderColor: "#E5D6B9",
        borderWidth: 1,
        borderRadius: radius.xs,
        paddingHorizontal: 8,
        paddingVertical: 5,
        flexDirection: "row",
        alignItems: "center",
        gap: 6
    },
    noticeText: {
        flex: 1,
        fontFamily: fonts.medium,
        fontSize: 11,
        lineHeight: 16,
        color: "#806B46"
    }
});
